import type { GridCoord } from '@antification/core';

/** A point in canvas pixels. */
export interface ScreenCoord {
  x: number;
  y: number;
}

/**
 * Where the mayor is looking.
 *
 * Pan, zoom, and the isometric projection between the grid the simulation
 * decides things in and the pixels a canvas draws in. This used to be six
 * members of `TerrainGrid` — `offsetX`, `offsetY`, `scale`, `gridToScreen`,
 * `zoomAtPoint`, `centerInViewport` — which made a camera part of a module a
 * headless settlement worker has to load, and made every screen coordinate in
 * the game reachable from a simulation method (ADR-009, closing Q14).
 *
 * Nothing here is state. Two clients watching the same district from different
 * zoom levels are watching the same district: no ant's fate, no car's route and
 * no building's plot depends on any number in this file, and the way that is
 * kept true is that the simulation cannot see it. The colony made the same move
 * in ADR-007 (D30) — *an exclusion is a promise, an absence is a compile error.*
 *
 * The projection is the same arithmetic it has always been, which is why
 * `tests/render.test.ts` records the same four hashes across the move.
 */
export class Camera {
  /**
   * The isometric footprint of one cell, in unscaled pixels.
   *
   * A diamond twice as wide as it is tall. `gridToScreen` gives a renderer the
   * centre of that diamond; these give it the size, and both are the camera's
   * business rather than the ground's.
   */
  readonly cellWidth = 32;
  readonly cellHeight = 16;

  private readonly minScale = 0.5;
  private readonly maxScale = 3;

  private offsetX = 0;
  private offsetY = 0;
  private scale = 1;

  /** The centre of cell (`gridX`, `gridY`) in canvas pixels. */
  gridToScreen(gridX: number, gridY: number): ScreenCoord {
    const isoX = (gridX - gridY) * (this.cellWidth / 2) * this.scale;
    const isoY = (gridX + gridY) * (this.cellHeight / 2) * this.scale;
    return {
      x: isoX + this.offsetX,
      y: isoY + this.offsetY,
    };
  }

  /**
   * The cell under a canvas pixel.
   *
   * The inverse projection only, with no opinion about whether that cell
   * exists — the grid's extent is the grid's to know, and a caller that needs a
   * real cell asks it for one (`ParkingtonLayer.screenToCell`).
   */
  screenToGrid(screenX: number, screenY: number): GridCoord {
    const localX = (screenX - this.offsetX) / this.scale;
    const localY = (screenY - this.offsetY) / this.scale;

    const gridX = (localX / (this.cellWidth / 2) + localY / (this.cellHeight / 2)) / 2;
    const gridY = (localY / (this.cellHeight / 2) - localX / (this.cellWidth / 2)) / 2;

    return { x: Math.floor(gridX), y: Math.floor(gridY) };
  }

  getScale(): number {
    return this.scale;
  }

  setScale(scale: number): void {
    this.scale = Math.max(this.minScale, Math.min(this.maxScale, scale));
  }

  getOffset(): ScreenCoord {
    return { x: this.offsetX, y: this.offsetY };
  }

  setOffset(x: number, y: number): void {
    this.offsetX = x;
    this.offsetY = y;
  }

  /**
   * Zoom about a screen point, keeping whatever is under it under it.
   *
   * @param screenX Screen X coordinate to zoom toward/from
   * @param screenY Screen Y coordinate to zoom toward/from
   * @param zoomFactor Factor to multiply scale by (>1 = zoom in, <1 = zoom out)
   */
  zoomAtPoint(screenX: number, screenY: number, zoomFactor: number): void {
    const oldScale = this.scale;
    const newScale = Math.max(this.minScale, Math.min(this.maxScale, oldScale * zoomFactor));

    if (newScale === oldScale) return;

    // The point in unscaled world coordinates before the zoom. The offset is
    // added after the isometric calculation, so it comes off first.
    const worldX = (screenX - this.offsetX) / oldScale;
    const worldY = (screenY - this.offsetY) / oldScale;

    // After the zoom that world point must land on the same screen point:
    //   screenX = worldX * newScale + newOffsetX
    this.offsetX = screenX - worldX * newScale;
    this.offsetY = screenY - worldY * newScale;
    this.scale = newScale;
  }

  /**
   * The offset that centres a `cols` × `rows` lot in a viewport.
   *
   * Separate from `centerInViewport` because the snap-back animation eases
   * toward it over ten frames rather than jumping to it, and used to recompute
   * it from a copy of this arithmetic with the map size hardcoded to 40 × 30.
   */
  centeredOffset(
    viewportWidth: number,
    viewportHeight: number,
    cols: number,
    rows: number
  ): ScreenCoord {
    const gridHeight = (cols + rows) * (this.cellHeight / 2) * this.scale;
    return {
      x: viewportWidth / 2,
      y: (viewportHeight - gridHeight) / 2 + 50, // Slight offset from top
    };
  }

  /** Centre a `cols` × `rows` lot in the viewport. */
  centerInViewport(
    viewportWidth: number,
    viewportHeight: number,
    cols: number,
    rows: number
  ): void {
    const centered = this.centeredOffset(viewportWidth, viewportHeight, cols, rows);
    this.offsetX = centered.x;
    this.offsetY = centered.y;
  }
}
